import type {Meta, StoryObj} from "@storybook/web-components";
import type {NotificationItem} from "../../src/components/notification-item/notification-item";
import {html} from "lit";

type SlotArgs = {message: string}; //! this is not a string but a slot that should hold (certain) html elements

const meta: Meta<NotificationItem & SlotArgs> = {
	title: "Notification/Notification Item",
	component: "uxn-notification-item",

	render: ({
		type,
		severity,
		status,

		headline,

		createdAt,
		willDeleteIn,
		willDeleteInTooltip,

		dateFormat,
		timeFormat,

		closedByName,
		closedByLabel,
		closeButtonText,

		link,
		linkText,

		source,
		sourceLink,

		notificationUpdateEndpoint,
		notificationUpdateErrorText,

		message,
	}) =>
		html`<uxn-notification-item
			type="${type}"
			severity="${severity}"
			status="${status}"
			headline="${headline}"
			created-at="${createdAt}"
			will-delete-in="${willDeleteIn}"
			will-delete-in-tooltip="${willDeleteInTooltip}"
			date-format="${dateFormat}"
			time-format="${timeFormat}"
			closed-by-name="${closedByName}"
			closed-by-label="${closedByLabel}"
			close-button-text="${closeButtonText}"
			link="${link}"
			link-text="${linkText}"
			source="${source}"
			source-link="${sourceLink}"
			notification-update-endpoint="${notificationUpdateEndpoint}"
			notification-update-error-text="${notificationUpdateErrorText}"
		>
			<span slot="message">${message}</span>
		</uxn-notification-item>`,

	argTypes: {
		type: {control: "select", options: ["Overview", "Detail"]},
		severity: {control: "select", options: ["Information", "Warning", "Error"]},
		status: {control: "select", options: ["Current", "Historic"]},
		headline: {control: "text"},

		createdAt: {control: "text"},
		willDeleteIn: {control: "text"},
		willDeleteInTooltip: {control: "text"},

		dateFormat: {control: "text"},
		timeFormat: {control: "text"},

		closedByName: {control: "text", if: {arg: "status", eq: "Historic"}},
		closedByLabel: {control: "text", if: {arg: "status", eq: "Historic"}},
		closeButtonText: {control: "text"},

		link: {control: "text", if: {arg: "type", eq: "Detail"}},
		linkText: {control: "text", if: {arg: "type", eq: "Detail"}},

		source: {control: "text", if: {arg: "type", eq: "Detail"}},
		sourceLink: {control: "text", if: {arg: "type", eq: "Detail"}},

		notificationUpdateEndpoint: {control: "text"},
		notificationUpdateErrorText: {control: "text"},

		//! these should be {summary: "html-elements"} instead of strings
		message: {
			name: '<slot name = "message" />',
			description: "body of the notification",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
	},
};

export default meta;
type Story = StoryObj<NotificationItem & SlotArgs>;

export const Default: Story = {
	args: {
		type: "Overview",
		severity: "Information",
		status: "Current",

		headline: "Juicy",

		createdAt: "1720016626",
		willDeleteIn: "3h",
		willDeleteInTooltip: "The notification is automatically deleted after the time indicated.",

		dateFormat: "DD.MM.YYYY",
		timeFormat: "HH:mm",

		closedByName: "Biggie Smalls",
		closedByLabel: "Closed by",
		closeButtonText: "Close Notification",

		link: "https://en.wikipedia.org/wiki/Juicy_(The_Notorious_B.I.G._song)",
		linkText: "read more...",

		source: "Wikipedia",
		sourceLink: "https://en.wikipedia.org/wiki/The_Notorious_B.I.G.",

		notificationUpdateEndpoint: "/mock-data/notification-item.json",
		notificationUpdateErrorText: "Update failed. Please try again.",

		message: `It was all a dream, I used to read Word Up! magazine. Salt-n-Pepa and Heavy D up in the limousine. Hangin' pictures on my wall, Every Saturday Rap Attack, Mr. Magic, Marley Marl.`,
	},
	parameters: {
		controls: {exclude: ["text"]},
	},
};

export const Type: Story = {
	args: {
		...Default.args,
	},
	parameters: {
		controls: {include: ["type", "linkText", "link", "source", "sourceLink"]},
	},
};

export const Severity: Story = {
	args: {
		...Default.args,
	},
	parameters: {
		controls: {include: ["severity"]},
	},
};

export const Status: Story = {
	args: {
		...Default.args,
	},
	parameters: {
		controls: {include: ["status", "closedByName", "closedByLabel"]},
	},
};

export const Content: Story = {
	args: {
		...Default.args,
		type: "Detail",
	},
	parameters: {
		controls: {include: ["headline", "message", "linkText", "link", "source", "sourceLink"]},
	},
};

export const Time: Story = {
	args: {
		...Default.args,
		type: "Detail",
	},
	parameters: {
		controls: {include: ["createdAt", "willDeleteIn", "willDeleteInTooltip", "dateFormat", "timeFormat"]},
	},
};
